import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { FormControl, InputLabel, MenuItem, Select, type SelectChangeEvent, Stack, Typography } from "@mui/material";

import TwoLineDropdownItem from "@/components/dropdown/two-line-dropdown-item";

export const scanToProduceOptions = Object.freeze({
	none: "None",
	scanToCreate: "ScanToCreate",
	scanToTriggerAll: "ScanToTrigger",
	scanToTriggerSingle: "ScanToTriggerSingle",
});

export type ScanToProduceOption = (typeof scanToProduceOptions)[keyof typeof scanToProduceOptions];

export const isScanToTriggerConfigured = (value: string | null | undefined): boolean =>
	value === scanToProduceOptions.scanToTriggerAll || value === scanToProduceOptions.scanToTriggerSingle;

export const isScanToTriggerSingleConfigured = (value: string | null | undefined): boolean =>
	value === scanToProduceOptions.scanToTriggerSingle;

interface ManualScanToProduceDropDownProps {
	readonly selectedOption: ScanToProduceOption | null | undefined;
	readonly handleChange: (value: ScanToProduceOption) => void;
}

const labelId = "manual-scan-to-produce-dropdown__label";

const ManualScanToProduceDropDown: React.FC<ManualScanToProduceDropDownProps> = ({ selectedOption, handleChange }) => {
	const intl = useIntl();

	return (
		<Stack gap={1}>
			<FormControl fullWidth>
				<InputLabel id={labelId}>
					<FormattedMessage id="Manual scan to produce" defaultMessage="Manual scan to produce" />
				</InputLabel>
				<Select
					labelId={labelId}
					label={intl.formatMessage({ id: "Manual scan to produce", defaultMessage: "Manual scan to produce" })}
					value={selectedOption || scanToProduceOptions.none}
					onChange={(e: SelectChangeEvent) => handleChange(e.target.value as ScanToProduceOption)}
					data-testid="manual-scan-to-produce-dropdown__select"
					data-pendoid="production-groups-configuration-modal-dropdown__select"
				>
					<MenuItem
						value={scanToProduceOptions.none}
						data-testid="manual-scan-to-produce-dropdown__option__none"
						data-pendoid="production-groups-configuration-modal-dropdown__option__none"
					>
						<FormattedMessage id="None" defaultMessage="None" />
					</MenuItem>
					<MenuItem
						value={scanToProduceOptions.scanToCreate}
						data-testid="manual-scan-to-produce-dropdown__option__scan-to-create"
						data-pendoid="production-groups-configuration-modal-dropdown__option__scan-to-create"
					>
						<TwoLineDropdownItem
							primaryText={intl.formatMessage({ id: "Scan to create", defaultMessage: "Scan to create" })}
							secondaryText={intl.formatMessage({
								id: "Produce a job from barcode content",
								defaultMessage: "Produce a job from barcode content",
							})}
						/>
					</MenuItem>
					<MenuItem
						value={scanToProduceOptions.scanToTriggerAll}
						data-testid="manual-scan-to-produce-dropdown__option__scan-to-trigger-all"
						data-pendoid="production-groups-configuration-modal-dropdown__option__scan-to-trigger-all"
					>
						<TwoLineDropdownItem
							primaryText={intl.formatMessage({
								id: "Scan to trigger (all)",
								defaultMessage: "Scan to trigger (all)",
							})}
							secondaryText={intl.formatMessage({
								id: "LPN trigger value releases matching batches",
								defaultMessage: "LPN trigger value releases matching batches",
							})}
						/>
					</MenuItem>
					<MenuItem
						value={scanToProduceOptions.scanToTriggerSingle}
						data-testid="manual-scan-to-produce-dropdown__option__scan-to-trigger-single"
						data-pendoid="production-groups-configuration-modal-dropdown__option__scan-to-trigger-single"
					>
						<TwoLineDropdownItem
							primaryText={intl.formatMessage({
								id: "Scan to trigger (single)",
								defaultMessage: "Scan to trigger (single)",
							})}
							secondaryText={intl.formatMessage({
								id: "LPN trigger value releases one job in a batch",
								defaultMessage: "LPN trigger value releases one job in a batch",
							})}
						/>
					</MenuItem>
				</Select>
			</FormControl>
			{Boolean(selectedOption) && selectedOption !== scanToProduceOptions.none && (
				<Typography variant="caption" color="text.secondary" sx={{ pl: 2 }}>
					<FormattedMessage
						id="*Must be configured in import selection"
						defaultMessage="*Must be configured in import selection"
					/>
				</Typography>
			)}
		</Stack>
	);
};

export default ManualScanToProduceDropDown;
